import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const ChevronRightIcon = () => (
   <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
   </svg>
);

const Submenu = ({ label, icon, paths, subItems, isOpen, toggleSidebar, onLinkClick }) => {
   const location = useLocation();
   const isAnyActive = paths.some((p) => location.pathname.startsWith(p));
   const [open, setOpen] = useState(isAnyActive);

   return (
      <div>
         <button
            onClick={() => {
               if (!isOpen) {
                  toggleSidebar();
                  setOpen(true);
               } else {
                  setOpen((prev) => !prev);
               }
            }}
            title={!isOpen ? label : ''}
            className={`w-full flex items-center rounded-lg px-2 py-2 transition-colors ${
               isAnyActive && !open
                  ? 'bg-[#0DCAF0] dark:bg-[#0DCAF0] text-white'
                  : 'text-gray-300 dark:text-gray-400 hover:bg-gray-700 dark:hover:bg-gray-600 hover:text-white'
            } ${!isOpen ? 'justify-center' : 'gap-3'}`}
         >
            <span className="flex-shrink-0">{icon}</span>
            {isOpen && (
               <>
                  <span className="font-medium flex-1 text-left">{label}</span>
                  <span className={`transition-transform duration-200 ${open ? 'rotate-90' : ''}`}>
                     <ChevronRightIcon />
                  </span>
               </>
            )}
         </button>

         {isOpen && open && (
            <div className="mt-1 ml-3 pl-3 border-l border-gray-600 dark:border-gray-500 space-y-1">
               {subItems.map((sub) => {
                  const isActive =
                     location.pathname === sub.path ||
                     (sub.matchPrefix && location.pathname.startsWith(sub.path));
                  return (
                     <Link
                        key={sub.path}
                        to={sub.path}
                        onClick={onLinkClick}
                        className={`flex items-center gap-2.5 rounded-lg px-2 py-2 transition-colors ${
                           isActive
                              ? 'bg-[#0DCAF0] dark:bg-[#0DCAF0] text-white'
                              : 'text-gray-400 dark:text-gray-500 hover:bg-gray-700 dark:hover:bg-gray-600 hover:text-white'
                        }`}
                     >
                        <span className="flex-shrink-0">{sub.icon}</span>
                        <span className="text-sm font-medium">{sub.label}</span>
                     </Link>
                  );
               })}
            </div>
         )}
      </div>
   );
};

const Sidebar = ({ isOpen, toggleSidebar }) => {
   const location = useLocation();

   const handleMobileClose = () => {
      if (window.innerWidth < 1024 && isOpen) toggleSidebar();
   };

   const topItems = [
      {
         path: '/admin',
         label: 'Dashboard',
         exact: true,
         icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
               />
            </svg>
         ),
      },
   ];

   const blogSubItems = [
      {
         path: '/admin/blogs',
         label: 'Blogs',
         matchPrefix: true,
         icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
               />
            </svg>
         ),
      },
      {
         path: '/admin/authors',
         label: 'Authors',
         matchPrefix: true,
         icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
               />
            </svg>
         ),
      },
      {
         path: '/admin/categories',
         label: 'Categories',
         icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
               />
            </svg>
         ),
      },
      {
         path: '/admin/tags',
         label: 'Tags',
         icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
               />
            </svg>
         ),
      },
   ];

   const inboxSubItems = [
      {
         path: '/admin/contact-messages',
         label: 'Contact Messages',
         icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
               />
            </svg>
         ),
      },
      {
         path: '/admin/bug-reports',
         label: 'Bug Reports',
         icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01M5.07 5.07A9 9 0 1 0 18.93 18.93 9 9 0 0 0 5.07 5.07z"
               />
            </svg>
         ),
      },
   ];

   const bottomItems = [
      {
         path: '/admin/proposals',
         label: 'Proposals',
         icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
               />
            </svg>
         ),
      },
      {
         path: '/admin/date-invites',
         label: 'Date Invites',
         icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
               />
            </svg>
         ),
      },
      {
         path: '/admin/polls',
         label: 'Polls',
         icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m0 0a2 2 0 002 2h2a2 2 0 002-2v-3a2 2 0 00-2-2h-2a2 2 0 00-2 2m0 0V4a2 2 0 012-2h0"
               />
            </svg>
         ),
      },
      {
         path: '/admin/documents',
         label: 'Documents',
         icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2zM13 3v5a1 1 0 001 1h5"
               />
            </svg>
         ),
      },
      {
         path: '/admin/projects',
         label: 'Projects',
         icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
               />
            </svg>
         ),
      },
      {
         path: '/admin/apps',
         label: 'Apps',
         icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
               />
            </svg>
         ),
      },
   ];

   const linkClass = (active) =>
      `flex items-center rounded-lg px-2 py-2 transition-colors ${
         active
            ? 'bg-[#0DCAF0] dark:bg-[#0DCAF0] text-white'
            : 'text-gray-300 dark:text-gray-400 hover:bg-gray-700 dark:hover:bg-gray-600 hover:text-white'
      }`;

   return (
      <aside
         className={`fixed left-0 top-0 z-40 h-screen bg-gray-800 dark:bg-dark-secondary text-white transition-all duration-300 ${isOpen ? 'w-64' : 'w-[56px]'} ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
         <div className="flex h-full flex-col">
            {/* Logo */}
            <div className="flex h-16 items-center justify-center border-b border-gray-700 dark:border-gray-600">
               {isOpen ? (
                  <div className="flex gap-[8px] items-center">
                     <img src="/images/logo/logo_.svg" className="w-[40px] h-full" alt="geekdas" />
                     <span className="text-[22px]">GeekDas</span>
                  </div>
               ) : (
                  <img src="/images/logo/logo_.svg" className="w-[30px] h-full" alt="geekdas" />
               )}
            </div>

            <nav className="flex-1 space-y-1 px-2 py-4 overflow-y-auto">
               {/* Top-level items */}
               {topItems.map((item) => {
                  const isActive = item.exact
                     ? location.pathname === item.path
                     : location.pathname.startsWith(item.path);
                  return (
                     <Link
                        key={item.path}
                        to={item.path}
                        onClick={handleMobileClose}
                        title={!isOpen ? item.label : ''}
                        className={`${linkClass(isActive)} ${!isOpen ? 'justify-center' : 'gap-3'}`}
                     >
                        <span className="flex-shrink-0">{item.icon}</span>
                        {isOpen && <span className="font-medium">{item.label}</span>}
                     </Link>
                  );
               })}

               {/* Blog group */}
               <Submenu
                  label="Blog"
                  icon={
                     <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth={2}
                           d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                        />
                     </svg>
                  }
                  paths={['/admin/blogs', '/admin/authors', '/admin/categories', '/admin/tags']}
                  subItems={blogSubItems}
                  isOpen={isOpen}
                  toggleSidebar={toggleSidebar}
                  onLinkClick={handleMobileClose}
               />

               {/* Inbox group */}
               <Submenu
                  label="Inbox"
                  icon={
                     <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth={2}
                           d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                        />
                     </svg>
                  }
                  paths={['/admin/contact-messages', '/admin/bug-reports']}
                  subItems={inboxSubItems}
                  isOpen={isOpen}
                  toggleSidebar={toggleSidebar}
                  onLinkClick={handleMobileClose}
               />

               {/* Bottom-level items */}
               {bottomItems.map((item) => {
                  const isActive = item.exact
                     ? location.pathname === item.path
                     : location.pathname.startsWith(item.path);
                  return (
                     <Link
                        key={item.path}
                        to={item.path}
                        onClick={handleMobileClose}
                        title={!isOpen ? item.label : ''}
                        className={`${linkClass(isActive)} ${!isOpen ? 'justify-center' : 'gap-3'}`}
                     >
                        <span className="flex-shrink-0">{item.icon}</span>
                        {isOpen && <span className="font-medium">{item.label}</span>}
                     </Link>
                  );
               })}
            </nav>
         </div>
      </aside>
   );
};

export default Sidebar;
